import React from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Paper, 
  Grid, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  LinearProgress, 
  IconButton, 
  Avatar,
  Button,
  Checkbox,
  FormControlLabel,
  alpha,
} from '@mui/material';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateDashboardTitleSx,
  harmonateKpiCardSx,
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
} from '../../theme/harmonateStyles';
import { 
  MoreVert as MoreVertIcon, 
  CheckCircle as CheckCircleIcon, 
  Error as ErrorIcon, 
  Warning as WarningIcon, 
  Circle as CircleIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement 
} from 'chart.js';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const positiveChipSx = {
  bgcolor: ht.secondaryTab,
  color: ht.text,
  fontWeight: 'bold',
  fontSize: '0.6rem',
  height: 16,
};

const tableHeadCellSx = {
  py: 0.25,
  borderBottom: `1px solid ${ht.line}`,
  color: ht.text,
  fontWeight: 500,
  fontSize: '0.65rem',
};

const tableBodyCellSx = {
  py: 0,
  borderBottom: `1px solid ${ht.line}`,
  fontSize: '0.65rem',
  color: ht.text,
};

const tableRowSx = {
  '&:hover': { bgcolor: ht.secondaryTab },
};

export default function AnomalyDetectionDashboard() {
  // Sample data for charts
  const lineChartData = {
    labels: ['1/7', '15/7', '30/7', '15/8', '30/8', '15/9', '30/9'],
    datasets: [
      {
        label: 'Current Year',
        data: [25, 40, 30, 45, 35, 55, 37],
        borderColor: ht.green,
        backgroundColor: alpha(ht.green, 0.1),
        fill: true,
        tension: 0.4
      },
      {
        label: 'Previous Year',
        data: [20, 35, 25, 40, 30, 45, 30],
        borderColor: ht.blue,
        backgroundColor: alpha(ht.blue, 0.1),
        fill: true,
        tension: 0.4
      }
    ]
  };

  const weeklyRevenueData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    datasets: [
      {
        label: 'Revenue',
        data: [12, 19, 15, 22, 15, 18],
        backgroundColor: ht.green
      }
    ]
  };

  const dailyTrafficData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Traffic',
        data: [18, 12, 24, 15, 22, 10],
        backgroundColor: ht.green
      }
    ]
  };

  const usageData = {
    labels: ['Active', 'Inactive'],
    datasets: [
      {
        data: [75, 25],
        backgroundColor: [ht.green, ht.line],
        borderWidth: 0
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: { display: false },
      y: { display: false }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    cutout: '70%'
  };

  // Sample data for the check table
  const checkTableData = [
    { name: 'Notification/APIS', amount: 12.34, cases: 8.65, status: 'normal' },
    { name: 'Accounts/Flags', amount: 9.58, cases: 4.53, status: 'error' },
    { name: 'Billing Updates', amount: 25.3, cases: 10.12, status: 'normal' },
    { name: 'Service ML Boost', amount: 18.8, cases: 8.41, status: 'normal' },
    { name: 'Analytics', amount: 7.5, cases: 3.55, status: 'normal' }
  ];

  // Sample recovery data
  const recoveryData = [
    { id: 'Anomaly 001', status: "Approved", date: "12 Apr 2023", progress: 90 },
    { id: 'Normal 002', status: "Denied", date: "15 Apr 2023", progress: 7 },
    { id: 'Anomaly 003', status: "Approved", date: "18 May 2023", progress: 50 },
    { id: 'Anomaly 004', status: "Warning", date: "02 Jun 2023", progress: 35 },
    { id: 'Anomaly 004', status: "Approved", date: "02 Jun 2023", progress: 86 },
    { id: 'Anomaly 004', status: "Warning", date: "02 Jun 2023", progress: 15 },
    { id: 'Anomaly 004', status: "Denied", date: "02 Jun 2023", progress: 32 },
    { id: 'Anomaly 004', status: "Approved", date: "02 Jun 2023", progress: 68 },
    { id: 'Anomaly 004', status: "Approved", date: "02 Jun 2023", progress: 45 },
    { id: 'Anomaly 004', status: "Approved", date: "02 Jun 2023", progress: 68 },
    { id: 'Anomaly 004', status: "Approved", date: "02 Jun 2023", progress: 79 }
  ];

  // Sample task data
  const taskData = [
    { name: 'Landing Page Design', count: 12 },
    { name: 'Dashboard Design', count: 8 },
    { name: 'Mobile App Design', count: 11 },
    { name: 'Illustrations', count: 5 },
    { name: 'Promotional Ads', count: 7 }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'normal':
        return <CheckCircleIcon sx={{ color: ht.green, fontSize: 16 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#E6A817', fontSize: 16 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: ht.red, fontSize: 16 }} />;
      default:
        return <CircleIcon sx={{ color: ht.fadedText, fontSize: 16 }} />;
    }
  };

  const getRecoveryStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircleIcon sx={{ color: ht.green, fontSize: 16 }} />;
      case 'Denied':
        return <ErrorIcon sx={{ color: ht.red, fontSize: 16 }} />;
      case 'Warning':
        return <WarningIcon sx={{ color: '#E6A817', fontSize: 16 }} />;
      default:
        return <CircleIcon sx={{ color: ht.fadedText, fontSize: 16 }} />;
    }
  };

  // Calendar days
  const calendarDays = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

  return (
    <Box sx={harmonateDashboardPageSx}>
      <Typography sx={{ ...harmonateDashboardTitleSx, mb: 2 }}>
        Anomaly Detection
      </Typography>

      {/* Main Content Grid */}
      <Grid container spacing={1} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Section - 33% width */}
        <Grid item xs={4}>
          {/* Top Stats Cards - NOW HORIZONTAL */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {/* Earnings */}
            <Grid item xs={4}>
              <Paper elevation={0} sx={{ ...harmonateKpiCardSx, p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ color: ht.green, fontSize: 14, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: ht.fadedText }}>Earnings</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>$350.4</Typography>
                  <Chip 
                    label="+4.75%" 
                    size="small" 
                    sx={{ ml: 0.5, ...positiveChipSx }} 
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Previous Month */}
            <Grid item xs={4}>
              <Paper elevation={0} sx={{ ...harmonateKpiCardSx, p: 1 }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Previous Month</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>$642.39</Typography>
              </Paper>
            </Grid>
            
            {/* Anomalies */}
            <Grid item xs={4}>
              <Paper elevation={0} sx={{ ...harmonateKpiCardSx, p: 1 }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Anomalies</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>$574.34</Typography>
                  <Chip 
                    label="+24%" 
                    size="small" 
                    sx={{ ml: 0.5, ...positiveChipSx }} 
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* This Month Chart */}
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: ht.fadedText }}>Monthly</Typography>
            <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>$37.5K</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
              <Chip 
                label="+2.45%" 
                size="small" 
                sx={{ ...positiveChipSx, mr: 0.5 }} 
              />
              <Typography variant="caption" sx={{ color: ht.fadedText, fontSize: '0.65rem' }}>vs last month</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
              <CircleIcon sx={{ color: ht.green, fontSize: 6, mr: 0.5 }} />
              <Typography variant="caption" sx={{ color: ht.fadedText, fontSize: '0.65rem' }}>On track</Typography>
            </Box>
            <Box sx={{ height: 100 }}>
              <Line 
                data={lineChartData} 
                options={{
                  ...chartOptions,
                  elements: {
                    point: { radius: 0 }
                  }
                }} 
              />
            </Box>
          </Paper>
          
          {/* Check Table */}
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography variant="caption" sx={{ color: ht.fadedText }}>Check Table</Typography>
              <IconButton size="small" sx={{ padding: 0 }}>
                <MoreVertIcon sx={{ color: ht.fadedText, fontSize: 14 }} />
              </IconButton>
            </Box>
            
            <TableContainer sx={{ maxHeight: 150 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>NAME</TableCell>
                    <TableCell sx={tableHeadCellSx}>AMOUNT</TableCell>
                    <TableCell sx={tableHeadCellSx}>CASES</TableCell>
                    <TableCell sx={tableHeadCellSx}>STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkTableData.map((row, index) => (
                    <TableRow key={index} sx={tableRowSx}>
                      <TableCell sx={tableBodyCellSx}>{row.name}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{row.amount}</TableCell>
                      <TableCell sx={tableBodyCellSx}>{row.cases}</TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, py: 0 }}>{getStatusIcon(row.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
        
        {/* Middle Section - 33% width */}
        <Grid item xs={4}>
          {/* Top Stats Cards - NOW HORIZONTAL */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {/* Target Value */}
            <Grid item xs={4}>
              <Paper elevation={0} sx={{ ...harmonateKpiCardSx, p: 1 }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Target Value</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>$1000</Typography>
              </Paper>
            </Grid>
            
            {/* New Tasks */}
            <Grid item xs={4}>
              <Paper elevation={0} sx={{ ...harmonateKpiCardSx, p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: ht.fadedText }}>New Tasks</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>154</Typography>
                </Box>
                <Avatar 
                  sx={{ 
                    width: 20, 
                    height: 20, 
                    bgcolor: 'transparent', 
                    border: `1px solid ${ht.line}` 
                  }}
                  variant="rounded"
                >
                  <img 
                    src="https://flagcdn.com/w20/us.png" 
                    alt="US Flag" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                  />
                </Avatar>
              </Paper>
            </Grid>
            
            {/* Total Flags */}
            <Grid item xs={4}>
              <Paper elevation={0} sx={{ ...harmonateKpiCardSx, p: 1 }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Total Flags</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.9rem' }}>2938</Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Weekly Revenue */}
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: ht.fadedText }}>Weekly Revenue</Typography>
            <Box sx={{ height: 163 }}>
              <Bar 
                data={weeklyRevenueData} 
                options={{
                  ...chartOptions,
                  barThickness: 10,
                  borderRadius: 2
                }} 
              />
            </Box>
          </Paper>
          
          {/* Daily Traffic and Usage Section */}
          <Grid container spacing={1}>
            {/* Daily Traffic */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, height: '100%' }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Daily Traffic</Typography>
                <Box sx={{ height: 120 }}>
                  <Bar 
                    data={dailyTrafficData} 
                    options={{
                      ...chartOptions,
                      barThickness: 6,
                      borderRadius: 2
                    }} 
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Usage */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, height: '100%' }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Usage</Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: ht.text, fontSize: '0.8rem' }}>
                    2,579
                    <Typography component="span" variant="caption" sx={{ ml: 0.5, fontSize: '0.65rem', color: ht.fadedText }}>
                      users
                    </Typography>
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', height: 90, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Pie data={usageData} options={pieChartOptions} />
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      color: ht.green
                    }}
                  >
                    75%
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: ht.fadedText, fontSize: '0.65rem' }}>
                    Active users
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        
        {/* Right Section - 33% width */}
        <Grid item xs={4}>
          {/* Recovery Table */}
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: ht.fadedText }}>Recovery</Typography>
            <TableContainer sx={{ maxHeight: 235 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>NAME</TableCell>
                    <TableCell sx={tableHeadCellSx}>STATUS</TableCell>
                    <TableCell sx={tableHeadCellSx}>DATE</TableCell>
                    <TableCell sx={tableHeadCellSx}>PROGRESS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recoveryData.map((row) => (
                    <TableRow key={row.id} sx={{ ...tableRowSx, '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ ...tableBodyCellSx, py: 0.25 }}>{row.id}</TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, py: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getRecoveryStatusIcon(row.status)}
                          <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.65rem', color: ht.text }}>{row.status}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...tableBodyCellSx, py: 0.25 }}>{row.date}</TableCell>
                      <TableCell sx={{ py: 0.25, borderBottom: `1px solid ${ht.line}`, width: '30%' }}>
                        <LinearProgress
                          variant="determinate"
                          value={row.progress}
                          sx={{
                            height: 3,
                            borderRadius: 2,
                            backgroundColor: ht.listBg,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: row.status === 'Approved' ? ht.green : ht.red
                            }
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Bottom Grid for Calendar and Tasks */}
          <Grid container spacing={1}>
            {/* Calendar */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, height: '124%' }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Calendar</Typography>
                <Grid container spacing={0.25}>
                  {calendarDays.map((day) => (
                    <Grid item xs={3} key={day}>
                      <Box 
                        sx={{ 
                          width: '100%', 
                          height: 16, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          borderRadius: 1,
                          ...(day === 12 && {
                            bgcolor: ht.green,
                            color: ht.white
                          })
                        }}
                      >
                        <Typography variant="caption" fontSize="0.65rem">{day}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
            
            {/* Tasks */}
            <Grid item xs={6}>
              <Paper elevation={0} sx={{ ...harmonateCardSx, p: 1, height: '124%' }}>
                <Typography variant="caption" sx={{ color: ht.fadedText }}>Tasks</Typography>
                <Box>
                  {taskData.slice(0, 5).map((task, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '20px' }}>
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            size="small" 
                            sx={{ 
                              color: ht.line, 
                              '&.Mui-checked': { color: ht.green },
                              padding: '1px'
                            }} 
                          />
                        } 
                        label={<Typography variant="body2" fontSize="0.65rem" sx={{ color: ht.text }}>{task.name}</Typography>}
                        sx={{ 
                          m: 0,
                          '& .MuiTypography-root': { fontSize: '0.65rem' } 
                        }}
                      />
                      <Typography variant="body2" sx={{ color: ht.text, fontSize: '0.65rem' }}>{task.count}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Footer actions — in-flow so page padding matches Tenants */}
      <Box
        sx={{
          mt: 'auto',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: ht.white,
          borderRadius: ht.cardRadius,
          boxShadow: ht.cardShadow,
          px: 2.5,
          py: 1.25,
        }}
      >
        <Button
          variant="contained"
          size="small"
          sx={{ ...harmonateProceedButtonSx, py: 0.25, fontSize: '0.75rem' }}
        >
          Download Dashboard
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={{ ...harmonateUploadOutlinedSx, py: 0.25, fontSize: '0.75rem' }}
        >
          Download Output
        </Button>
      </Box>
    </Box>
  );
}